
import React from "react";
import { Clock, User, AlertCircle, FileText, Tag, Flag } from "lucide-react";
import { EditHistory } from "@/hooks/useNotificationEditHistory";
import { 
  Accordion, 
  AccordionContent, 
  AccordionItem, 
  AccordionTrigger 
} from "@/components/ui/accordion";
import { User as AuthUser } from "@/context/AuthContext";
import { getStatusDisplayName } from "@/utils/statusMappings";

interface NotificationEditHistoryProps {
  editHistory: EditHistory[];
  currentUser?: AuthUser | null;
}

const NotificationEditHistory = ({ editHistory, currentUser }: NotificationEditHistoryProps) => {
  if (!editHistory || editHistory.length === 0) {
    return (
      <div className="text-sm text-muted-foreground italic">
        Geen bewerkingsgeschiedenis beschikbaar
      </div>
    );
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('nl-NL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Function to get appropriate icon for change type
  const getChangeTypeIcon = (edit: EditHistory) => {
    const changeType = edit.change_type || 'message_edited';
    
    switch (changeType) {
      case 'status_changed':
        return <AlertCircle className="h-4 w-4 text-orange-500" />;
      case 'type_changed':
        return <Tag className="h-4 w-4 text-blue-500" />;
      case 'priority_changed':
        return <Flag className="h-4 w-4 text-red-500" />;
      case 'message_edited':
      default:
        return <FileText className="h-4 w-4 text-green-500" />;
    }
  };

  // Function to get change description
  const getChangeDescription = (edit: EditHistory) => {
    const changeType = edit.change_type || 'message_edited';
    const fieldName = edit.field_name || 'message';
    
    switch (changeType) {
      case 'status_changed':
        return `Status gewijzigd van "${getStatusDisplayName(edit.previous_value || '')}" naar "${getStatusDisplayName(edit.new_value || '')}"`;
      case 'type_changed':
        return `Type gewijzigd van "${edit.previous_value || ''}" naar "${edit.new_value || ''}"`;
      case 'priority_changed':
        return `Prioriteit gewijzigd van "${edit.previous_value || ''}" naar "${edit.new_value || ''}"`;
      case 'vo_number_changed':
        return `VO nummer gewijzigd van "${edit.previous_value || ''}" naar "${edit.new_value || ''}"`;
      case 'exact_order_number_changed':
        return `Exact ordernummer gewijzigd van "${edit.previous_value || ''}" naar "${edit.new_value || ''}"`;
      case 'message_edited':
        return "Bericht bewerkt";
      default:
        return `${fieldName} gewijzigd`;
    }
  };

  return (
    <div className="space-y-2">
      <h3 className="text-sm font-medium">Bewerkingsgeschiedenis</h3>
      <Accordion type="single" collapsible className="w-full">
        {editHistory.map((edit) => {
          const isCurrentUserEdit = currentUser && edit.editor_id === currentUser.id;
          const changeIcon = getChangeTypeIcon(edit);
          const changeDescription = getChangeDescription(edit);
          
          return (
            <AccordionItem key={edit.id} value={edit.id}>
              <AccordionTrigger className="py-2 text-sm hover:no-underline">
                <div className="flex items-center gap-2 text-left">
                  <User className={`h-4 w-4 ${isCurrentUserEdit ? "text-blue-600" : "text-muted-foreground"}`} />
                  <span className={isCurrentUserEdit ? "font-semibold text-blue-600" : ""}>
                    {isCurrentUserEdit ? `${edit.editor_name} (jij)` : edit.editor_name}
                  </span>
                  <Clock className="h-4 w-4 ml-2 text-muted-foreground" />
                  <span className="text-muted-foreground">
                    {formatDate(edit.edit_date)}
                  </span>
                  <span className="ml-2 flex items-center gap-1">
                    {changeIcon}
                    <span className="text-xs">{changeDescription}</span>
                  </span>
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <div className="space-y-2 pt-2">
                  {edit.change_type === 'message_edited' ? (
                    <>
                      {edit.previous_message && (
                        <div>
                          <h4 className="text-xs font-medium text-muted-foreground mb-1">Vorige versie:</h4>
                          <div className="p-2 bg-muted rounded-md text-xs font-mono whitespace-pre-wrap">
                            {edit.previous_message}
                          </div>
                        </div>
                      )}
                      <div>
                        <h4 className="text-xs font-medium text-muted-foreground mb-1">Nieuwe versie:</h4>
                        <div className="p-2 bg-muted rounded-md text-xs font-mono whitespace-pre-wrap">
                          {edit.new_message}
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div>
                        <h4 className="text-xs font-medium text-muted-foreground mb-1">Vorige waarde:</h4>
                        <div className="p-2 bg-muted rounded-md text-xs">
                          {edit.field_name === 'status' ? getStatusDisplayName(edit.previous_value || '') : edit.previous_value || '(Leeg)'}
                        </div>
                      </div>
                      <div>
                        <h4 className="text-xs font-medium text-muted-foreground mb-1">Nieuwe waarde:</h4>
                        <div className="p-2 bg-muted rounded-md text-xs">
                          {edit.field_name === 'status' ? getStatusDisplayName(edit.new_value || '') : edit.new_value || '(Leeg)'}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>
    </div>
  );
};

export default NotificationEditHistory;
